import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import NoteDetail from '../../components/display/NoteDetail';
import { useAuth } from '../../contexts/AuthContext';
import {
  NOTE_DETAIL_DESCRIPTION,
  NOTE_DETAIL_TITLE,
  NOTE_NOT_FOUND_TITLE,
  useDocumentHead,
} from '../../seo/documentHead';

// This would be in a real API service
import { notesService } from '../../services/notesService';

// Updated params type to be more flexible
interface NotePageParams extends Record<string, string | undefined> {
  id?: string;
  publicId?: string;
}

const NotePage: React.FC = () => {
  const params = useParams<NotePageParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [note, setNote] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isHearted, setIsHearted] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);

  useEffect(() => {
    const loadNote = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        let noteData;

        if (location.pathname.startsWith('/notes/') && params.publicId) {
          noteData = await notesService.fetchPublicNoteContentByPublicId(params.publicId);
        } else if (location.pathname.startsWith('/my-notes/') && params.id) {
          noteData = await notesService.fetchNote(params.id);
        } else {
          setError('Invalid note route or parameters.');
          setIsLoading(false);
          return;
        }
        
        setNote(noteData);

        // Check if current user is the owner of the note
        const noteOwnerUserId = noteData?.contributorInfo?.userId;
        const currentUserId = currentUser?.id;
        setIsOwner(!!(noteOwnerUserId && currentUserId && noteOwnerUserId === currentUserId));

        // TODO: Get actual hearted status from API - for now defaulting to false
        // This should be returned by the API as part of the note data for authenticated users
        setIsHearted(false);
      } catch (err) {
        console.error('Error fetching note:', err);
        setError('Failed to load the note. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (params.publicId || params.id) {
      loadNote();
    } else {
        setError('Note identifier missing from route.');
        setIsLoading(false);
    }
  }, [params.id, params.publicId, location.pathname]);

  // Note pages are always noindex: notes are user-submitted and moderators can
  // edit or unpublish them, so only the /notes list is offered to search
  // engines. The title and description are fixed copy, never the note text.
  useDocumentHead(
    isLoading
      ? null
      : note && !error
        ? { title: NOTE_DETAIL_TITLE, description: NOTE_DETAIL_DESCRIPTION, noindex: true }
        : { title: NOTE_NOT_FOUND_TITLE, noindex: true }
  );

  const handleHeartClick = async () => {
    if (!note || !(note as any)._id) return;
    
    try {
      await notesService.heartNote((note as any)._id);
      setIsHearted((prev: boolean) => !prev);
      setNote((prev: any) => ({
        ...prev,
        heart_count: isHearted ? prev.heart_count - 1 : prev.heart_count + 1
      }));
    } catch (err) {
      console.error('Error hearting note:', err);
    }
  };

  const handleFlagClick = async () => {
    if (!note || !(note as any)._id) return;
    try {
      await notesService.flagNote((note as any)._id, 'Inappropriate content');
      alert('Note flagged for review.');
    } catch (err) {
      console.error('Error flagging note:', err);
      alert('Failed to flag note.');
    }
  };

  const handleShareClick = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert('Link copied to clipboard!'))
      .catch(err => console.error('Failed to copy link:', err));
  };

  const handleEditClick = () => {
    if (!note || !(note as any)._id) return;
    navigate(`/my-notes/edit/${(note as any)._id}`);
  };

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          {isLoading ? (
            <div className="text-center my-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading note...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : note ? (
            <NoteDetail
              note={note}
              isOwner={isOwner}
              isAdmin={!!(currentUser?.role === 'admin' || currentUser?.role === 'moderator')}
              onHeartClick={handleHeartClick}
              onFlagClick={handleFlagClick}
              isHearted={isHearted}
              onShareClick={handleShareClick}
              onEditClick={handleEditClick}
            />
          ) : (
            <div className="alert alert-warning">Note not found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotePage; 